import { useState } from 'react';
import { ConfirmActionModal } from '@shared/components';
import { useReauthoriseAuthorisedContactMutation } from '../../../hooks/useReauthoriseAuthorisedContactMutation';
import type { AuthorisedContact } from '../../../types/authorisedContacts.types';

interface Props {
    agreementId: string;
    contact: AuthorisedContact | null;
    onClose: () => void;
}

/** The revocation is cleared on the row; that it happened at all survives on the
 *  agreement's timeline, which is append-only. */
export function ReauthoriseAuthorisedContactDialog({ agreementId, contact, onClose }: Props) {
    const [formError, setFormError] = useState<string | null>(null);
    const mutation = useReauthoriseAuthorisedContactMutation(agreementId);

    const handleClose = () => {
        setFormError(null);
        onClose();
    };

    const handleConfirm = () => {
        if (!contact) return;
        setFormError(null);
        mutation.mutate(contact.id, {
            onSuccess: handleClose,
            onError: () => setFormError('Failed to re-authorise the contact. Please try again.'),
        });
    };

    return (
        <ConfirmActionModal
            isOpen={contact !== null}
            title="Re-authorise contact"
            message={
                <>
                    <p>
                        {contact
                            ? `${contact.name} will be authorised to speak for the plan holder again. The revocation stays on the agreement's history.`
                            : ''}
                    </p>
                    {formError && (
                        <p role="alert" className="mt-3 text-sm text-mp-error">
                            {formError}
                        </p>
                    )}
                </>
            }
            confirmLabel="Re-authorise"
            confirmVariant="primary"
            isPending={mutation.isPending}
            onConfirm={handleConfirm}
            onClose={handleClose}
        />
    );
}
